import { Button, Css, Palette, ScrollableContent, useTestIds } from "@homebound/beam";
import { snakeCase } from "change-case";
import { useParams } from "react-router";
import { ReadyPlanAssetType } from "src/generated/graphqlTypes";
import { EmptyState } from "src/routes/components/EmptyState";
import {
  basementLines,
  bathroomLines,
  bedroomLines,
  garageLines,
  generalLines,
  PlanDataCard,
  squareFootageLines,
} from "src/routes/developments/plan-and-options/PlanDetailsPage";
import { PageHeader } from "src/routes/layout/PageHeader";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { PRODUCT_EMPTY_STATE_IMG_URL } from "src/routes/libraries/product-catalog/components/product-images-viewer/ProductImageViewer";
import {
  StructureLotSummaryPage_PlanPackageFragment,
  StructureLotSummaryPage_ProductOfferingFragment,
  StructureLotSummaryPage_ReadyPlanOptionFragment,
  useProjectLotSummaryPageQuery,
  useStructureLotSummaryPageQuery,
} from "src/routes/projects/lot-summary/ProjectLotSummaryPage.generated";
import { ProjectParams } from "src/routes/routesDef";
import { createProjectFinishScheduleUrl, createProjectProductOfferingAppliableConfigsUrl } from "src/RouteUrls";
import { useProjectContext } from "../context/ProjectContext";
import { EmptyPlansDetailImage } from "../expenses/components/EmptyStateSvgs";

export function ProjectLotSummaryPage() {
  const { __typename } = useProjectContext();
  const { projectId } = useParams<ProjectParams>();

  if (__typename === "Structure") {
    return <StructureLotSummaryPageView projectId={projectId} />;
  }
  // Default to ProjectLotSummaryPageView for non-structure projects
  return <ProjectLotSummaryPageView projectId={projectId} />;
}

type ProjectLotSummaryPageViewProps = { projectId: string };

export function ProjectLotSummaryPageView(props: ProjectLotSummaryPageViewProps) {
  const tid = useTestIds({}, "ProjectLotSummaryPage");
  const { projectId } = props;
  const { data, loading } = useProjectLotSummaryPageQuery({ variables: { projectId } });
  const prpc = data?.project.readyPlanConfig;
  const programData = prpc?.programData;
  const elevation = prpc?.elevationOption?.readyPlanOption;

  return (
    <>
      <PageHeader
        title={loading ? `...` : (prpc?.readyPlan?.name ?? "")}
        right={
          <>
            <Button label="Finish Schedule" onClick={createProjectFinishScheduleUrl(projectId)} variant="tertiary" />
          </>
        }
      />
      <ScrollableContent>
        <div css={Css.bgGray100.mh100.pxPx(60).$}>
          <div css={Css.mb4.$} {...tid.elevation}>
            {!loading && programData && (
              <h1 css={Css.xl.gray900.pb3.$} {...tid.elevationTitle}>
                Elevation & Square Footages
              </h1>
            )}
            {!loading &&
              !elevation &&
              (programData ? (
                <EmptyState svg={<EmptyPlansDetailImage />} message="There are no elevations selected yet" />
              ) : (
                <EmptyState svg={<EmptyPlansDetailImage />} message="There are no lot configurations yet" />
              ))}
            {!loading && elevation && (
              <div css={Css.dg.gtc("repeat(auto-fill, 535px)").gap3.add("alignContent", "flex-start").$}>
                <PlanDataCard
                  key={elevation.id}
                  tid={tid}
                  lines={squareFootageLines(programData)}
                  title="Square Footage"
                  subtitle={`${elevation.globalOption.code} - ${elevation.globalOption.name}`}
                  icon="ruler"
                  tooltip="TBD"
                />
              </div>
            )}
          </div>
          <div css={Css.pb3.$}>
            {!loading && programData && (
              <>
                <h1 css={Css.xl.gray900.pb3.$} {...tid.OtherTitle}>
                  Bed, Bath, Garage
                </h1>
                <div css={Css.df.gap3.add("flexWrap", "wrap").$}>
                  <div css={Css.df.fdc.gap3.$}>
                    <PlanDataCard
                      tid={tid}
                      lines={generalLines(programData, undefined, prpc?.readyPlan?.description)}
                      title="General"
                      icon="projects"
                    />
                    <PlanDataCard tid={tid} lines={bedroomLines(programData)} title="Bedrooms" icon="bed" />
                    <PlanDataCard tid={tid} lines={bathroomLines(programData)} title="Bathrooms" icon="bath" />
                  </div>
                  <div css={Css.df.fdc.gap3.$}>
                    <PlanDataCard
                      tid={tid}
                      lines={basementLines(programData)}
                      title="Basement"
                      icon="basement"
                      alignIcon={true}
                    />
                    <PlanDataCard tid={tid} lines={garageLines(programData)} title="Garage" icon="car" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </ScrollableContent>
    </>
  );
}

function StructureLotSummaryPageView(props: ProjectLotSummaryPageViewProps) {
  const tid = useTestIds({}, "StructureLotSummaryPageView");
  const { projectId } = props;
  const { data, loading } = useStructureLotSummaryPageQuery({ variables: { projectId } });
  const prpc = data?.project.readyPlanConfig;
  const programData = prpc?.programData;
  const readyPlanOptions = prpc?.readyPlanOptions ?? [];
  const { readyPlan } = prpc ?? {};
  const productOffering =
    readyPlan && readyPlan.__typename === "ProductOffering"
      ? (readyPlan as StructureLotSummaryPage_ProductOfferingFragment)
      : undefined;

  const elevation = readyPlanOptions.find((option) => option.type.isElevation);
  const specLevel = readyPlanOptions.find((option) => option.type.isSpecLevel);
  const interiorScheme = readyPlanOptions.find((option) => option.type.forDesignInterior);
  const exteriorScheme = readyPlanOptions.find((option) => option.type.isExteriorPalette);
  const orientation = prpc?.orientationDetail;

  return (
    <>
      <PageHeader
        title={loading ? `...` : "Plan Info"}
        right={
          <>
            <Button
              label="View Finish Schedule"
              onClick={createProjectFinishScheduleUrl(projectId)}
              variant="tertiary"
            />
            <Button
              label="Edit"
              onClick={createProjectProductOfferingAppliableConfigsUrl(projectId)}
              variant="primary"
            />
          </>
        }
      />
      <ScrollableContent>
        <div css={Css.bgGray100.mh100.pxPx(60).$}>
          <div css={Css.mb4.$} {...tid.generalLines}>
            {!loading && programData && (
              <>
                <h1 css={Css.xl.gray900.pb3.$} {...tid.elevationTitle}>
                  General
                </h1>
                <div css={Css.df.gap3.add("flexWrap", "wrap").$}>
                  <div css={Css.df.fdc.gap3.$}>
                    <PlanCard planPackage={productOffering?.planPackage} />
                  </div>
                  <div css={Css.df.fdc.gap3.$}>
                    <OptionCard rpo={elevation!} displayName="Elevation" />
                  </div>
                  <div css={Css.df.fdc.gap3.$}>
                    <OptionCard rpo={specLevel!} displayName="Spec Level" />
                  </div>
                  <div css={Css.df.fdc.gap3.$}>
                    <OptionCard rpo={interiorScheme!} displayName="Interior Scheme" />
                  </div>
                  <div css={Css.df.fdc.gap3.$}>
                    <OrientationCard orientation={orientation?.name ?? "No Orientation Selected"} />
                  </div>
                  <div css={Css.df.fdc.gap3.$}>
                    <OptionCard rpo={exteriorScheme!} displayName="Exterior Scheme" />
                  </div>
                </div>
              </>
            )}
          </div>

          <div css={Css.mb4.$} {...tid.elevation}>
            {!loading && programData && (
              <h1 css={Css.xl.gray900.pb3.$} {...tid.elevationTitle}>
                Elevation & Square Footages
              </h1>
            )}
            {!loading &&
              !elevation &&
              (programData ? (
                <EmptyState svg={<EmptyPlansDetailImage />} message="There are no elevations selected yet" />
              ) : (
                <EmptyState svg={<EmptyPlansDetailImage />} message="There are no lot configurations yet" />
              ))}
            {!loading && elevation && (
              <div css={Css.dg.gtc("repeat(auto-fill, 535px)").gap3.add("alignContent", "flex-start").$}>
                <PlanDataCard
                  key={elevation.id}
                  tid={tid}
                  lines={squareFootageLines(programData)}
                  title="Square Footage"
                  subtitle={`${elevation.globalOption.name} - ${elevation.globalOption.name}`}
                  icon="ruler"
                  tooltip="TBD"
                />
              </div>
            )}
          </div>

          <div css={Css.pb3.$}>
            {!loading && programData && (
              <>
                <h1 css={Css.xl.gray900.pb3.$} {...tid.OtherTitle}>
                  Bed, Bath, Garage
                </h1>
                <div css={Css.df.gap3.add("flexWrap", "wrap").$}>
                  <div css={Css.df.fdc.gap3.$}>
                    <PlanDataCard
                      tid={tid}
                      lines={generalLines(programData, undefined, productOffering?.description)}
                      title="General"
                      icon="projects"
                    />
                    <PlanDataCard tid={tid} lines={bedroomLines(programData)} title="Bedrooms" icon="bed" />
                    <PlanDataCard tid={tid} lines={bathroomLines(programData)} title="Bathrooms" icon="bath" />
                  </div>
                  <div css={Css.df.fdc.gap3.$}>
                    <PlanDataCard
                      tid={tid}
                      lines={basementLines(programData)}
                      title="Basement"
                      icon="basement"
                      alignIcon={true}
                    />
                    <PlanDataCard tid={tid} lines={garageLines(programData)} title="Garage" icon="car" />
                  </div>
                </div>
              </>
            )}
          </div>

          <div css={Css.pb3.$}>
            {!loading && readyPlanOptions && (
              <>
                <h1 css={Css.xl.gray900.pb3.$} {...tid.OtherTitle}>
                  Options
                </h1>
                <SelectedOptions readyPlanOptions={readyPlanOptions} tid={tid} />
              </>
            )}
          </div>
        </div>
      </ScrollableContent>
    </>
  );
}

export function PlanCard(props: { planPackage: StructureLotSummaryPage_PlanPackageFragment | undefined }) {
  const tid = useTestIds({});
  const { planPackage } = props;

  const name = planPackage?.name ?? "<None Selected>";
  const displayName = "Plan";

  const coverPhotoUrl =
    planPackage?.assets?.find((asset) => asset?.type.code === ReadyPlanAssetType.CoverPhoto)?.asset.previewUrl ??
    PLAN_FALLBACK_IMG;

  return (
    <div css={Css.wPx(535).p3.bgWhite.br8.bshBasic.$} data-testid={snakeCase(`section_${displayName.toLowerCase()}`)}>
      <div
        key={planPackage?.id}
        css={Css.dg.gtc("96px 1fr 24px").gtr("20px 1fr").cg2.mb4.add("transition", "opacity 0.3s ease").$}
      >
        <img
          css={Css.gc(1).gr("1 / 3").ba.br8.bcGray300.objectFit("cover").wPx(96).hPx(96).$}
          src={coverPhotoUrl ?? PRODUCT_EMPTY_STATE_IMG_URL}
          alt={planPackage?.name}
          {...tid}
        />

        <h3 css={Css.gr(1).gc(2).xsSb.gray700.$}>{displayName}</h3>
        <div css={Css.gr(2).gc(2).$}>
          <div css={Css.df.gap1.aic.jcsb.smSb.gray900.$} {...tid.opt}>
            <h4 css={Css.$}>{name}</h4>
          </div>
        </div>
      </div>
    </div>
  );
}

export function OrientationCard(props: { orientation: string }) {
  const tid = useTestIds({});
  const { orientation } = props;

  return (
    <div css={Css.wPx(535).p3.bgWhite.br8.bshBasic.$} data-testid={snakeCase(`section_orientation`)}>
      <div
        key={orientation}
        css={Css.dg.gtc("96px 1fr 24px").gtr("20px 1fr").cg2.mb4.add("transition", "opacity 0.3s ease").$}
      >
        <OriginationImage orientation={orientation} />
        <h3 css={Css.gr(1).gc(2).xsSb.gray700.$}>Orientation</h3>
        <div css={Css.gr(2).gc(2).$}>
          <div css={Css.df.gap1.aic.jcsb.smSb.gray900.$} {...tid.opt}>
            <h4 css={Css.$}>{[orientation].compact().join(" - ")}</h4>
          </div>
        </div>
      </div>
    </div>
  );
}

export function OriginationImage({ orientation }: { orientation: string }) {
  const tid = useTestIds({}, "originationImage");
  const color = Palette.Gray100;
  const imageSizePx = 96;

  return (
    <div
      css={Css.gc(1).gr("1 / 3").ba.br8.bcGray300.bgColor(color).wPx(imageSizePx).hPx(imageSizePx).fs0.df.aic.jcc.$}
      {...tid}
    >
      <span css={Css.xl.gray600.$}>{orientation}</span>
    </div>
  );
}

export function OptionCard(props: {
  displayName: string;
  rpo: StructureLotSummaryPage_ReadyPlanOptionFragment | undefined;
}) {
  const tid = useTestIds({});
  const { rpo, displayName } = props;

  const name = rpo?.name ?? "<None Selected>";
  return (
    <div css={Css.wPx(535).p3.bgWhite.br8.bshBasic.$} data-testid={snakeCase(`section_${displayName.toLowerCase()}`)}>
      <div
        key={rpo?.id}
        css={Css.dg.gtc("96px 1fr 24px").gtr("20px 1fr").cg2.mb4.add("transition", "opacity 0.3s ease").$}
      >
        <OptionImage rpo={rpo} />
        <h3 css={Css.gr(1).gc(2).xsSb.gray700.$}>{displayName}</h3>
        <div css={Css.gr(2).gc(2).$}>
          <div css={Css.df.gap1.aic.jcsb.smSb.gray900.$} {...tid.opt}>
            <h4 css={Css.$}>{[name, rpo?.globalOption.code, rpo?.location?.name].compact().join(" - ")}</h4>
          </div>
        </div>
      </div>
    </div>
  );
}

export function OptionImage({ rpo }: { rpo: StructureLotSummaryPage_ReadyPlanOptionFragment | undefined }) {
  const tid = useTestIds({}, "optionImage");
  const imageSizePx = 96;

  // For design packages, display the initials of the option name as we don't expect to have an option image
  if (rpo?.type.forDesignInterior) {
    const color = rpo.globalOption.group.designPackageTagColor ?? Palette.Purple500;

    return (
      <div
        css={Css.gc(1).gr("1 / 3").ba.br8.bcGray300.bgColor(color).wPx(imageSizePx).hPx(imageSizePx).fs0.df.aic.jcc.$}
        {...tid}
      >
        <span css={Css.xl.white.$}>{getOptionInitials(rpo.name)}</span>
      </div>
    );
  }

  return (
    <img
      css={Css.gc(1).gr("1 / 3").ba.br8.bcGray300.objectFit("cover").wPx(imageSizePx).hPx(imageSizePx).$}
      src={rpo?.globalOption.asset?.previewUrl ?? PRODUCT_EMPTY_STATE_IMG_URL}
      alt={rpo?.name}
      {...tid}
    />
  );
}

export function SelectedOptions({
  readyPlanOptions,
  tid,
}: {
  readyPlanOptions: StructureLotSummaryPage_ReadyPlanOptionFragment[];
  tid: ReturnType<typeof useTestIds>;
}) {
  if (readyPlanOptions.length === 0) {
    return <EmptyState svg={<EmptyPlansDetailImage />} message="There are no options selected yet" />;
  }

  const groupedRPOs = readyPlanOptions.sortBy((rpo) => rpo.type.order).groupBy((rpo) => rpo.type.name);

  return (
    <div css={Css.df.fdc.gap3.$}>
      {Object.entries(groupedRPOs).map(([typeDisplayName, rpoGroup]) => (
        <div key={typeDisplayName} css={Css.df.fdc.gap2.$}>
          <h2 css={Css.xl.gray900.$} {...tid[typeDisplayName]}>
            {typeDisplayName}
          </h2>
          <div css={Css.df.fdc.gap3.$}>
            <div css={Css.df.gap3.add("flexWrap", "wrap").$}>
              {rpoGroup.map((rpo) => (
                <OptionCard key={rpo.id} rpo={rpo} displayName={rpo.type.name} />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Returns the first two letters of each word, capitalized. Example: "Mountain Contemporary" -> "Mo Co"
function getOptionInitials(name: string) {
  return name
    .split(" ")
    .map((word) => {
      const firstTwo = word.slice(0, 2);
      return firstTwo.charAt(0).toUpperCase() + firstTwo.slice(1).toLowerCase();
    })
    .join(" ");
}
